"use client"

import { Button } from "@mdxui/primitives"
import { motion, useInView, useReducedMotion } from "framer-motion"
import { useRef } from "react"
import { Check } from 'lucide-react'
import { cn } from "../../lib/cn"
import { Markdown } from "../../lib/markdown"
import {
  gridContainerVariants,
  listItemVariants,
  reducedMotionVariants,
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
} from "../../lib/motion"
import { CodeWindowBase } from "../code-window"

/**
 * FeaturesSplitCode block props (split-code variant - content left, code right with gradient overlay)
 *
 * @remarks
 * **CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Code window ONLY - no image support, NO copy button (gradient overlay covers it)
 * - Uses `theme="auto"` - code window bg uses theme variables (bg-background), not hardcoded colors
 * - Gradient overlays use `from-background` to seamlessly fade edges (always matches code window)
 * - Check icons (no circles) for feature bullets
 * - Supports primary/secondary button group below bullets
 * - Best for: showcasing code examples with feature highlights
 */
export interface FeaturesSplitCodeProps {
  /** Main heading */
  heading?: string
  /** Description paragraph */
  description?: string
  /** Feature bullet items with check icons */
  features?: string[]
  /** Primary action button */
  primaryButton?: { text: string; url: string }
  /** Secondary action button */
  secondaryButton?: { text: string; url: string }
  /** Button position relative to features list */
  buttonPosition?: "above" | "below"
  /** Code to display */
  code?: {
    code: string
    language: string
  }
  className?: string
}

const defaultCode = `import { Button } from '@mdxui/primitives'
import { Hero } from '@mdxui/neo'

export default function LandingPage() {
  return (
    <Hero
      title="Build faster with MDXUI"
      description="Production-ready components"
      callToAction="Get Started"
    />
  )
}`

const FeaturesSplitCode = ({
  heading = "Transform an Idea into Reality",
  description = "Unleash your creativity and break through barriers. Our platform brings all your ideas together in one intuitive workspace.",
  features = [
    "Advanced Design Templates",
    "Collaborative Editing",
    "Resource Libraries",
    "Real-time Feedback Tools",
    "Custom Asset Export",
    "Version History",
  ],
  primaryButton,
  secondaryButton,
  buttonPosition = "below",
  code = { code: defaultCode, language: "typescript" },
  className,
}: FeaturesSplitCodeProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromLeftVariants
  const codeAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromRightVariants
  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : listItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn("overflow-x-hidden py-16 sm:py-24 lg:py-32", className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          {/* Content - Left */}
          <motion.div
            className="w-full min-w-0 max-w-full"
            variants={contentAnimation}
          >
            <h2 className="mb-5 font-semibold text-4xl lg:text-5xl">
              {heading}
            </h2>
            <div
              className={cn(
                buttonPosition === "above" ? "mb-8" : "mb-8 lg:mb-12",
              )}
            >
              <Markdown size="base">{description}</Markdown>
            </div>
            {/* Button group - above */}
            {buttonPosition === "above" &&
              (primaryButton || secondaryButton) && (
                <motion.div
                  className="mb-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mb-12"
                  variants={itemAnimation}
                >
                  {primaryButton && (
                    <Button asChild className="w-full sm:w-auto">
                      <a href={primaryButton.url}>{primaryButton.text}</a>
                    </Button>
                  )}
                  {secondaryButton && (
                    <Button
                      variant="outline"
                      asChild
                      className="w-full sm:w-auto"
                    >
                      <a href={secondaryButton.url}>{secondaryButton.text}</a>
                    </Button>
                  )}
                </motion.div>
              )}
            <motion.ul
              className="grid max-w-xl gap-4 sm:grid-cols-2"
              variants={gridContainerVariants(0.05, 0.1)}
            >
              {features.map((feature) => (
                <motion.li
                  key={feature}
                  className="flex min-w-0 items-center gap-2"
                  variants={itemAnimation}
                >
                  <Check className="size-5 shrink-0 text-primary" />
                  <p className="text-foreground break-words text-sm font-medium">
                    {feature}
                  </p>
                </motion.li>
              ))}
            </motion.ul>
            {/* Button group - below */}
            {buttonPosition === "below" &&
              (primaryButton || secondaryButton) && (
                <motion.div
                  className="mt-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mt-12"
                  variants={itemAnimation}
                >
                  {primaryButton && (
                    <Button asChild className="w-full sm:w-auto">
                      <a href={primaryButton.url}>{primaryButton.text}</a>
                    </Button>
                  )}
                  {secondaryButton && (
                    <Button
                      variant="outline"
                      asChild
                      className="w-full sm:w-auto"
                    >
                      <a href={secondaryButton.url}>{secondaryButton.text}</a>
                    </Button>
                  )}
                </motion.div>
              )}
          </motion.div>

          {/* Code Window - Right with CSS mask fade (no color matching needed) */}
          <motion.div
            className="relative min-w-0 overflow-hidden lg:overflow-visible"
            variants={codeAnimation}
          >
            <CodeWindowBase
              code={code.code}
              language={code.language}
              showHeader={false}
              showCopy={false}
              theme="auto"
              style="borderless"
              className="min-h-[400px] max-h-[500px] w-full rounded-[var(--radius-2xl,calc(var(--radius)*2))] border border-border lg:max-h-none [mask-image:linear-gradient(to_bottom,black_60%,transparent_100%),linear-gradient(to_right,black_70%,transparent_100%)] [mask-composite:intersect] [-webkit-mask-composite:source-in]"
            />
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesSplitCode }
